<template>
  <v-row dense class="mb-4">
    <template v-if="loading">
      <v-col v-for="n in 3" :key="'skeleton-' + n" cols="12" sm="6" md="4">
        <v-card flat rounded="lg" class="metric-card border">
          <v-card-text class="pa-4">
            <div class="d-flex align-center">
              <div class="skeleton-icon"></div>
              <div class="ml-3 flex-grow-1">
                <div class="skeleton-title"></div>
                <div class="skeleton-value mt-2"></div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </template>

    <!-- Metrics -->
    <template v-else>
      <v-col v-for="(item, index) in displayItems" :key="index" :sm="items.length >= 4 ? 3 : items.length === 3 ? 4 : 6">
        <v-card flat rounded="lg" class="metric-card border">
          <v-card-text class="pa-4">
            <div class="d-flex align-center">
              <div class="metric-icon" :class="getIconClass(item)">
                <v-icon size="20" :color="getIconColor(item)">
                  {{ item.icon }}
                </v-icon>
              </div>
              <div class="metric-content ml-3">
                <div class="metric-title">
                  {{ item.title }}
                </div>
                <div class="metric-value mt-2">
                  {{ item.currency ? `₹${item.count}` : item.count }}
                </div>
              </div>
            </div>
            <div v-if="item.subtitle" class="metric-subtitle mt-3">
              {{ item.subtitle }}
            </div>
            <div v-if="item.trend && item.percentage" class="d-flex align-center mt-3">
              <span class="trend-badge" :class="item.trend === 'up' ? 'trend-up' : 'trend-down'">
                <v-icon size="13">
                  {{ item.trend === "up" ? "mdi-arrow-up" : "mdi-arrow-down" }}
                </v-icon>
                {{ item.percentage }}%
              </span>
              <span class="trend-label ml-2"> vs last month </span>
              <v-tooltip v-if="item.previousMonthValue || item.currentMonthValue" location="top">
                <template #activator="{ props }">
                  <v-icon v-bind="props" size="15" class="trend-info ml-1">
                    mdi-information-outline
                  </v-icon>
                </template>
                <div class="trend-tooltip">
                  <div>
                    Previous Month:
                    <strong>
                      {{ item.previousMonthValue ?? "-" }}
                    </strong>
                  </div>

                  <div>
                    Current Month:
                    <strong>
                      {{ item.currentMonthValue ?? "-" }}
                    </strong>
                  </div>
                </div>
              </v-tooltip>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- View All -->
      <v-col v-if="items.length > 4" cols="12" class="d-flex justify-center align-center mt-2">
        <div class="view-all-toggle" @click="showAll = !showAll">
          <span>
            {{ showAll ? "View Less" : "View All" }}
          </span>
          <v-icon size="18">
            {{ showAll ? "mdi-chevron-up" : "mdi-chevron-down" }}
          </v-icon>
        </div>
      </v-col>
    </template>
  </v-row>
</template>

<script>
import { mapState } from "pinia";
import { useUiStore } from "@/store";

export default {
  name: "InformationCard",
  props: {
    items: { type: Array, required: true },
  },
  data() {
    return {
      showAll: false,
    };
  },
  computed: {
    ...mapState(useUiStore, { loading: "getLoading" }),

    displayItems() {
      return this.showAll ? this.items : this.items.slice(0, 4);
    },
  },

  methods: {
    getIconClass(item) {
      if (item.trend === "up") {
        return "icon-success";
      }
      if (item.trend === "down") {
        return "icon-error";
      }
      return "icon-primary";
    },

    getIconColor(item) {
      if (item.trend === "up") {
        return "success";
      }
      if (item.trend === "down") {
        return "error";
      }
      return "primary";
    },
  },
};
</script>

<style scoped>
.metric-card {
  min-height: 85px;
  background: #ffffff !important;
  border: 1px solid #e1e5eb !important;
  border-radius: 10px !important;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04) !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.metric-card:hover {
  transform: translateY(-2px);
  border-color: #d5dbe4 !important;
  box-shadow: 0 5px 14px rgba(16, 24, 40, 0.08) !important;
}
.metric-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  border-radius: 9px;
}

.icon-success {
  background: #eaf8f0;
}

.icon-error {
  background: #fff0ef;
}

.icon-primary {
  background: #edf2f7;
}

.metric-content {
  min-width: 0;
}

.metric-title {
  color: #7b8492;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.2;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.metric-value {
  color: #252a34;
  font-size: 21px;
  font-weight: 700;
  line-height: 1.2;
}

.metric-subtitle {
  color: #969daa;
  font-size: 11px;
  line-height: 1.3;
}

.trend-badge {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 3px 6px;
  border-radius: 5px;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
}

.trend-up {
  color: #218b4d;
  background: #eaf8f0;
}

.trend-down {
  color: #d14343;
  background: #fff0ef;
}

.trend-label {
  color: #969daa;
  font-size: 11px;
}

.trend-info {
  color: #9ca3af;
  cursor: pointer;
}

.trend-tooltip {
  font-size: 11px;
  line-height: 1.8;
}
.view-all-toggle {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #596273;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  user-select: none;
  transition: color 0.2s ease, transform 0.2s ease;
}

.view-all-toggle:hover {
  color: rgb(var(--v-theme-primary));
  transform: translateY(-1px);
}

.skeleton-icon {
  width: 42px;
  height: 42px;
  flex-shrink: 0;
  border-radius: 9px;
}

.skeleton-title {
  width: 90px;
  height: 12px;
  border-radius: 4px;
}

.skeleton-value {
  width: 70px;
  height: 23px;
  border-radius: 5px;
}

.skeleton-icon,
.skeleton-title,
.skeleton-value{
  background: linear-gradient(90deg, #e9edf2 25%, #f5f6f8 50%, #e9edf2 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
}

@keyframes skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }

  100% {
    background-position: -200% 0;
  }
}
.v-theme--dark .metric-card {
  background: #1c2027 !important;
  border-color: #303640 !important;
}

.v-theme--dark .metric-card:hover {
  border-color: #3a414d !important;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.25) !important;
}

.v-theme--dark .metric-title {
  color: #8e97a5;
}

.v-theme--dark .metric-value {
  color: #f0f2f5;
}

.v-theme--dark .metric-subtitle,
.v-theme--dark .trend-label {
  color: #858d9a;
}

.v-theme--dark .icon-primary {
  background: #282e37;
}

.v-theme--dark .icon-success {
  background: rgba(76, 175, 80, 0.12);
}

.v-theme--dark .icon-error {
  background: rgba(244, 67, 54, 0.11);
}
</style>